/**
 * `formatMoney` originally from Shopify’s `theme-scripts` package
 * https://github.com/Shopify/theme-scripts/blob/master/packages/theme-currency/currency.js#L20
 */

/**
 * Format money values based on your shop currency settings
 * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
 * or 3.00 dollars
 * @param  {String} formatString - shop money_format setting
 * @return {String} value - formatted value
 */

function formatMoney(cents, formatString = theme.moneyFormat) {
  if (typeof cents === 'undefined' || cents === null) {
    cents = 0;
  }

  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) {
    if (isNaN(number) || number === null || number === undefined) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

window.switchDOMContentLoaded = false;

document.addEventListener('DOMContentLoaded', () => {
  window.switchDOMContentLoaded = true;
});

window.switchOnDOMContentLoaded = (cb) => {
  if (cb) {
    if (window.switchDOMContentLoaded) {
      cb();
      return;
    }

    document.addEventListener('DOMContentLoaded', () => {
      cb();
    });
  } else {
    return new Promise((resolve) => {
      if (window.switchDOMContentLoaded) {
        resolve();
        return;
      }

      document.addEventListener(
        'DOMContentLoaded',
        () => {
          resolve();
        },
        {
          once: true,
        }
      );
    });
  }
};

window.requestIdleCallback =
  window.requestIdleCallback ||
  function (cb) {
    const start = Date.now();
    return setTimeout(function () {
      cb({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50 - (Date.now() - start));
        },
      });
    }, 1);
  };

window.cancelIdleCallback =
  window.cancelIdleCallback ||
  function (id) {
    clearTimeout(id);
  };

function throttle(callback, limit) {
  var waiting = false; // Initially, we're not waiting
  return function () {
    // We return a throttled function
    if (!waiting) {
      // If we're not waiting
      callback.apply(this, arguments); // Execute users function
      waiting = true; // Prevent future invocations
      setTimeout(function () {
        // After a period of time
        waiting = false; // And allow future invocations
      }, limit);
    }
  };
}

/**
 * Wrapper around Object.assign that deletes null or undefined
 * keys from the provided object, making them fall back to
 * values in the defaults object.
 *
 *
 * @param {Object} defaults - An object with defaults for settings/config
 * @param {Object} provided - Provided settings/config object
 * @returns {Object} - Merged object
 */

function objectWithDefaults(defaults, provided) {
  filterObjectByValues(provided, (value) => {
    return value === null || value === undefined;
  });

  return Object.assign(defaults, provided);
}

function wrap(el, tagName = 'div') {
  const wrapper = document.createElement(tagName);

  el.parentNode.insertBefore(wrapper, el);

  wrapper.appendChild(el);

  return wrapper;
}

function wrapAll(nodes, wrapper) {
  // Cache the current parent and previous sibling of the first node.
  var parent = nodes[0].parentNode;
  var previousSibling = nodes[0].previousSibling;

  // Place each node in wrapper.
  //  - If nodes is an array, we must increment the index we grab from
  //    after each loop.
  //  - If nodes is a NodeList, each node is automatically removed from
  //    the NodeList when it is removed from its parent with appendChild.
  for (var i = 0; nodes.length - i; wrapper.firstChild === nodes[0] && i++) {
    wrapper.appendChild(nodes[i]);
  }

  // Place the wrapper just after the cached previousSibling
  parent.insertBefore(wrapper, previousSibling.nextSibling);

  return wrapper;
}

function unwrap(wrapper) {
  // place childNodes in document fragment
  var docFrag = document.createDocumentFragment();
  while (wrapper.firstChild) {
    var child = wrapper.removeChild(wrapper.firstChild);
    docFrag.appendChild(child);
  }

  // replace wrapper with document fragment
  wrapper.parentNode.replaceChild(docFrag, wrapper);
}

function initTeleport(el) {
  if (!el) return;

  const teleportCandidates = el.querySelectorAll('[data-should-teleport]');

  if (teleportCandidates.length) {
    teleportCandidates.forEach((teleportCandidate) => {
      teleportCandidate.setAttribute(
        'x-teleport',
        teleportCandidate.dataset.shouldTeleport
      );
    });
  }
}

async function fetchSectionHTML(url, selector) {
  const res = await fetch(url);
  const fetchedSection = await res.text();
  const result = querySelectorInHTMLString(selector, fetchedSection);
  if (result === null) {
    return null;
  } else {
    return result.innerHTML;
  }
}

function fetchConfigDefaults(acceptHeader = 'application/json') {
  return {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json;',
      Accept: acceptHeader,
    },
  };
}

function parseDOMFromString(htmlString) {
  window.___shapesDOMParser = window.___shapesDOMParser || new DOMParser();

  return window.___shapesDOMParser.parseFromString(htmlString, 'text/html');
}

function querySelectorInHTMLString(selector, htmlString) {
  const parsedDOM = parseDOMFromString(htmlString);
  let result = parsedDOM.querySelector(selector);

  if (result) {
    return result;
  }

  const templateEls = parsedDOM.querySelectorAll('template');

  for (const templateEl of templateEls) {
    result = templateEl.content.querySelector(selector);

    if (result) {
      return result;
    }
  }

  return null;
}

const CART_STATIC_SECTION_IDS = [
  'cart-items',
  'cart-footer',
  'cart-item-count',
  'cart-live-region',
];
const CART_SECTION_RESPONSE_MODE_DRAWER = 'drawer-section';
const CART_SECTION_RESPONSE_MODE_STATIC = 'static-sections';

function getCartDrawerSectionId() {
  return window.theme?.cartSectionIds?.['cart-drawer'] || null;
}

function getCartRenderContext() {
  const cartType = window.theme?.settings?.cartType || 'page';
  const isCartPage =
    window.location.pathname === window.theme?.routes?.cart_url;
  const drawerSectionId = getCartDrawerSectionId();
  const useCartDrawer = cartType === 'modal' && !isCartPage;
  const useStaticSectionFallback = useCartDrawer && !drawerSectionId;

  return {
    cartType,
    isCartPage,
    drawerSectionId,
    useCartDrawer,
    useStaticSectionFallback,
  };
}

function getCartSectionRequestIds() {
  const { useCartDrawer, drawerSectionId, useStaticSectionFallback } =
    getCartRenderContext();

  if (useStaticSectionFallback) {
    if (!window.__shapesCartDrawerSectionFallbackLogged) {
      console.error(
        'Modal cart is active but the cart drawer section id is unavailable. Falling back to static cart sections for this update.'
      );
      window.__shapesCartDrawerSectionFallbackLogged = true;
    }

    return [...CART_STATIC_SECTION_IDS];
  }

  if (useCartDrawer) {
    return [drawerSectionId, 'cart-item-count', 'cart-live-region'];
  }

  return [...CART_STATIC_SECTION_IDS];
}

function getCartSectionResponseMode(responseSections) {
  const { useCartDrawer, drawerSectionId } = getCartRenderContext();

  if (useCartDrawer && drawerSectionId && responseSections?.[drawerSectionId]) {
    return CART_SECTION_RESPONSE_MODE_DRAWER;
  }

  return CART_SECTION_RESPONSE_MODE_STATIC;
}

function getCartDrawerTemplateContent(responseSections) {
  const { drawerSectionId } = getCartRenderContext();

  if (!drawerSectionId) return null;

  const drawerSectionHTML = responseSections?.[drawerSectionId];

  if (!drawerSectionHTML) return null;

  const drawerDOM = parseDOMFromString(drawerSectionHTML);
  const drawerTemplate = drawerDOM.querySelector(
    'template[data-should-teleport]'
  );

  return drawerTemplate?.content || null;
}

function getCartSectionHTML(responseSections, target) {
  if (!responseSections || !target) return null;

  switch (target) {
    case 'count':
      return responseSections['cart-item-count'] || null;
    case 'liveRegion':
      return responseSections['cart-live-region'] || null;
    case 'items':
    case 'footer': {
      const responseMode = getCartSectionResponseMode(responseSections);
      const selector =
        target === 'items' ? '[data-cart-items]' : '[data-cart-footer]';

      if (responseMode === CART_SECTION_RESPONSE_MODE_DRAWER) {
        const drawerContent = getCartDrawerTemplateContent(responseSections);
        return drawerContent?.querySelector(selector)?.outerHTML || null;
      }

      const sectionId = target === 'items' ? 'cart-items' : 'cart-footer';
      const sectionHTML = responseSections[sectionId];

      if (!sectionHTML) return null;

      return (
        querySelectorInHTMLString(selector, sectionHTML)?.outerHTML || null
      );
    }
    default:
      return null;
  }
}

/**
 * Light wrapper around fetch for making common
 * requests easier. Provides very basic caching.
 *
 * Requests are not cached inside theme editor.
 */

window.__fetchCache = window.__fetchCache || {};

const RESPONSE_TYPE_JSON = 0;
const RESPONSE_TYPE_TEXT = 1;

async function fetchAndCache(
  url,
  options,
  cacheTimeout = 5000,
  forceFresh = Shopify.designMode ? true : false,
  responseType
) {
  const key = url.toString();

  if (__fetchCache[key] && !forceFresh) {
    return __fetchCache[key];
  }

  const responseReader =
    responseType === RESPONSE_TYPE_TEXT
      ? Response.prototype.text
      : Response.prototype.json;

  const res = await fetch(url, options);
  const data = responseReader.call(res);

  if (cacheTimeout && cacheTimeout > 0) {
    __fetchCache[key] = data;

    setTimeout(() => {
      delete __fetchCache[key];
    }, cacheTimeout);
  }

  return data;
}

function invalidateCacheForKeysContaining(string) {
  const matchingKeys = Object.keys(__fetchCache).filter((key) =>
    key.includes(string)
  );

  for (const key of matchingKeys) {
    delete __fetchCache[key];
  }
}

/**
 * fetchHTML and fetchJSON cache responses for 5 seconds
 * by default; if a fresh request is required, set
 * forceFresh to true or use the freshHTML and freshJSON
 * helper functions.
 */

async function fetchHTML(
  url,
  options,
  cacheTimeout = 5000,
  forceFresh = Shopify.designMode ? true : false
) {
  return fetchAndCache(
    url,
    options,
    cacheTimeout,
    forceFresh,
    RESPONSE_TYPE_TEXT
  );
}

function freshHTML(url, options) {
  return fetchHTML(url, options, 0, true);
}

async function fetchJSON(
  url,
  options,
  cacheTimeout = 5000,
  forceFresh = Shopify.designMode ? true : false
) {
  return fetchAndCache(
    url,
    options,
    cacheTimeout,
    forceFresh,
    RESPONSE_TYPE_JSON
  );
}

function freshJSON(url, options) {
  return fetchJSON(url, options, 0, true);
}

async function fetchHTMLFragment(url, selector) {
  const fetchedHTMLString = await fetchHTML(url);
  const fragment = querySelectorInHTMLString(selector, fetchedHTMLString);

  return fragment ? fragment.innerHTML : '';
}

function mdBreakpointMQL() {
  return window.matchMedia('(min-width: 768px)');
}

function isMdBreakpoint() {
  return window.mdBreakpointMQL().matches;
}

function maxMdBreakpointMQL() {
  return window.matchMedia('(max-width: 767px)');
}

function isMaxMdBreakpoint() {
  return window.maxMdBreakpointMQL().matches;
}

function maxLgBreakpointMQL() {
  return window.matchMedia('(max-width: 989px)');
}

function isMaxLgBreakpoint() {
  return window.maxLgBreakpointMQL().matches;
}

function lgBreakpointMQL() {
  return window.matchMedia('(min-width: 990px)');
}

function isLgBreakpoint() {
  return window.lgBreakpointMQL().matches;
}

function motionSafeMQL() {
  return window.matchMedia('(prefers-reduced-motion: reduce)');
}

function isMotionSafe() {
  return !window.motionSafeMQL().matches;
}

function reducedDataMQL() {
  return window.matchMedia('(prefers-reduced-data: reduce)');
}

function prefersReducedData() {
  return reducedDataMQL().matches;
}

function isSlowConnection() {
  if ('connection' in navigator) {
    const { connection } = navigator;

    return (
      connection.effectiveType === 'slow-2g' ||
      connection.effectiveType === '2g'
    );
  }

  return false;
}

function shouldTryToSaveData() {
  return (
    prefersReducedData() ||
    isSlowConnection() ||
    ('connection' in navigator && navigator.connection.saveData === true)
  );
}

function kebabCase(subject) {
  if ([' ', '_'].includes(subject)) return subject;
  return subject
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[_\s]/, '-')
    .toLowerCase();
}

function clearURLSearchParams(url) {
  for (const key of [...url.searchParams.keys()]) {
    url.searchParams.delete(key);
  }
}

/**
 * paramsInput can be a string, a sequence of pairs,
 * or a record, as per:
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/URLSearchParams#examples
 *
 * It can also be an instance of URLSearchParams.
 *
 */

function _getURLByModifyingParams(
  urlString,
  paramsInput,
  clear = false,
  append
) {
  const url = new URL(urlString, window.location.origin);

  if (clear) {
    clearURLSearchParams(url);
  }

  const params = new URLSearchParams(paramsInput);

  const setOrAppendParam = append
    ? URLSearchParams.prototype.append
    : URLSearchParams.prototype.set;

  for (const [key, value] of params) {
    setOrAppendParam.call(url.searchParams, key, value);
  }

  return url;
}

function getURLWithParams(url, paramsInput, clear = false) {
  return _getURLByModifyingParams(url, paramsInput, clear, false);
}

function currentURLWithParams(paramsInput, clear = false) {
  return getURLWithParams(window.location.href, paramsInput, clear);
}

function getURLAddingParams(url, paramsInput, clear = false) {
  return _getURLByModifyingParams(url, paramsInput, clear, true);
}

function currentURLAddingParams(paramsInput, clear = false) {
  return getURLAddingParams(window.location.href, paramsInput, clear);
}

function iFrameCommand(iFrameEl, commandString) {
  if (!iFrameEl || !commandString) return;

  iFrameEl.contentWindow.postMessage(
    JSON.stringify({
      event: 'command',
      func: commandString,
      args: '',
    }),
    '*'
  );
}

function iFrameMethod(iFrameEl, methodString) {
  if (!iFrameEl || !methodString) return;

  iFrameEl.contentWindow.postMessage(
    JSON.stringify({
      method: methodString,
    }),
    '*'
  );
}

let touchDevice = false;

window.setTouch = function () {
  touchDevice = true;
};

window.isTouch = function () {
  return touchDevice;
};

async function getModalLabel(modalSlotName, slotEl) {
  if (Alpine.store('modals')[modalSlotName].open) {
    await globalNextTick();

    const labelSourceEl = Array.from(slotEl.children).filter((el) =>
      el.hasAttribute('data-modal-label')
    )[0];

    if (labelSourceEl) {
      return labelSourceEl.dataset.modalLabel;
    }
  }

  return false;
}

function waitForContent(element = null) {
  return new Promise((resolve, reject) => {
    let fallbackTimeoutId;

    if (!element) {
      const err = new Error('waitForContent: No element provided');
      err.element = null;

      reject(err);
      return;
    }

    if (element.innerHTML.trim().length > 0) {
      resolve();
      return;
    }

    const mutationObserver = new MutationObserver(
      (_mutationsList, observer) => {
        if (element.innerHTML.trim().length > 0) {
          observer.disconnect();
          clearTimeout(fallbackTimeoutId);
          resolve();
        }
      }
    );

    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    fallbackTimeoutId = setTimeout(() => {
      mutationObserver.disconnect();

      if (element.innerHTML.trim().length > 0) {
        resolve();
        return;
      }

      const err = new Error(
        'waitForContent: Awaited content was not received after 10 seconds, aborting'
      );
      err.element = element;

      reject(err);
    }, 10000);
  });
}

function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function asyncTimeout(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function globalNextTick() {
  return new Promise((resolve) => {
    queueMicrotask(resolve);
  });
}

function rafTick() {
  return new Promise((resolve) => {
    requestAnimationFrame(resolve);
  });
}

function getSectionId(el) {
  if (!el._closestSectionId) {
    el._closestSectionId = el
      .closest('.shopify-section')
      ?.getAttribute('id')
      .replace('shopify-section-', '');
  }

  return el._closestSectionId;
}

function daysInMs(days) {
  return days * 24 * 60 * 60 * 1000;
}

function msInDays(ms) {
  return ms / 1000 / 60 / 60 / 24;
}

function isInTheFuture(msSinceEpoch) {
  return msSinceEpoch > Date.now();
}

function setExpiringStorageItem(key, value, expiresIn) {
  localStorage.setItem(
    key,
    JSON.stringify({ value, expires: Date.now() + expiresIn })
  );
}

function getExpiringStorageItem(key) {
  const value = localStorage.getItem(key);

  if (!value) {
    return null;
  }

  let valueObject;

  try {
    valueObject = JSON.parse(value);
  } catch (e) {}

  if (valueObject && valueObject.expires) {
    if (isInTheFuture(valueObject.expires)) {
      return valueObject.value;
    } else {
      localStorage.removeItem(key);
      return null;
    }
  }

  return null;
}

function getRecentlyViewedProducts() {
  let currentRecentlyViewedProducts;

  const localStorageItem = localStorage.getItem(
    'switch-recently-viewed-products'
  );

  try {
    const parsedItem = JSON.parse(localStorageItem);

    currentRecentlyViewedProducts = Array.isArray(parsedItem) ? parsedItem : [];
  } catch (e) {
    currentRecentlyViewedProducts = [];
  }

  return currentRecentlyViewedProducts;
}

function updateRecentlyViewedProducts(productId) {
  if (!productId) return;

  const currentRecentlyViewedProducts = getRecentlyViewedProducts().filter(
    (id) => id !== productId
  );

  currentRecentlyViewedProducts.unshift(productId);

  localStorage.setItem(
    'switch-recently-viewed-products',
    JSON.stringify(currentRecentlyViewedProducts)
  );
}

function reevaluateScriptsIn(el) {
  const scriptEls = el.querySelectorAll('script');

  for (const scriptEl of scriptEls) {
    const newScriptEl = document.createElement('script');

    for (const attributeName of scriptEl.getAttributeNames()) {
      newScriptEl.setAttribute(
        attributeName,
        scriptEl.getAttribute(attributeName)
      );
    }

    newScriptEl.textContent = scriptEl.textContent;

    scriptEl.replaceWith(newScriptEl);
  }
}

window.replaceMainContentAbortController = null;

/* GLO-MOD 2026-10-19 (Hagan: "a flicker like the site is reloading" on
   every family swatch tap): the swap replaced the page the moment the HTML
   arrived, then the new product photos took another 300-450ms to load, so
   shoppers saw blank image boxes. Swaps now run inside a View Transition:
   the browser keeps showing the OLD page while the new one is inserted and
   its on-screen photos finish loading (capped at 800ms so a slow
   connection never stalls the tap), then cross-fades. Browsers without
   View Transitions (iOS < 18) swap exactly as before. */
function gpWaitForImages(rootEl, timeoutMs = 800) {
  const imgs = Array.from(
    rootEl.querySelectorAll('.product-media img, .media-thumbnail img')
  ).filter((img) => {
    const r = img.getBoundingClientRect();
    return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight;
  });
  const pending = imgs
    .filter((img) => !(img.complete && img.naturalWidth))
    .map(
      (img) =>
        new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        })
    );
  if (!pending.length) return Promise.resolve();
  return Promise.race([
    Promise.all(pending),
    new Promise((resolve) => setTimeout(resolve, timeoutMs))
  ]);
}

async function gpRunSwap(swap) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (document.startViewTransition && !reduce) {
    const transition = document.startViewTransition(swap);
    await transition.updateCallbackDone.catch(() => {});
  } else {
    await swap();
  }
}

async function replaceMainContent(
  url,
  options = undefined,
  cacheFor = 0,
  onReplaced = (_newEl, _oldEl) => {}
) {
  if (!url) return;

  window.replaceMainContentAbortController?.abort();
  window.replaceMainContentAbortController = new AbortController();
  const gpThisSwap = window.replaceMainContentAbortController;

  const html = await fetchHTML(
    url,
    { ...options, signal: window.replaceMainContentAbortController.signal },
    cacheFor,
    false
  ).catch((e) => {
    if (e.name === 'AbortError') {
      return;
    } else {
      throw e;
    }
  });

  const mainEl = document.querySelector('main#MainContent');

  const newMainEl = querySelectorInHTMLString('main#MainContent', html);

  if (newMainEl) {
    // a newer tap already took over - let it win
    if (window.replaceMainContentAbortController !== gpThisSwap) return;

    await gpRunSwap(async () => {
      window.Alpine?.destroyTree(mainEl);

      destroySlideshowsIn(mainEl);

      mainEl.replaceWith(newMainEl);

      reevaluateScriptsIn(newMainEl);

      discoverNewSlideshows(newMainEl);

      await gpWaitForImages(newMainEl);
    });

    await globalNextTick();

    onReplaced(newMainEl, mainEl);

    const historyURLAsString = url.toString();

    window.history.replaceState(
      { path: historyURLAsString },
      '',
      historyURLAsString
    );
  }

  window.replaceMainContentAbortController = null;
}

async function replaceElement(
  el,
  url,
  options = undefined,
  cacheFor = 0,
  onReplaced = (_newEl, _oldEl) => {}
) {
  if (!el || !url) return;

  if (!el.getAttribute('id')) {
    console.error('replaceElement needs an element with an id');
    return;
  }

  el.updateAbortController?.abort();
  el.updateAbortController = new AbortController();
  const gpThisSwap = el.updateAbortController;

  const html = await fetchHTML(
    url,
    { ...options, signal: el.updateAbortController.signal },
    cacheFor,
    false
  ).catch((e) => {
    if (e.name === 'AbortError') {
      return;
    } else {
      throw e;
    }
  });

  if (!html) return;

  const dom = parseDOMFromString(html);

  const newEl = dom.getElementById(el.getAttribute('id'));

  if (newEl) {
    /* GLO-MOD 2026-10-19: same View Transition swap as replaceMainContent */
    if (el.updateAbortController !== gpThisSwap) return;

    await gpRunSwap(async () => {
      window.Alpine?.destroyTree(el);

      destroySlideshowsIn(el);

      el.replaceWith(newEl);

      reevaluateScriptsIn(newEl);

      discoverNewSlideshows(newEl);

      await gpWaitForImages(newEl);
    });

    await globalNextTick();

    onReplaced(newEl, el);
  }
}

function isInView(element) {
  const bounding = element.getBoundingClientRect();

  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <=
      (window.innerWidth || document.documentElement.clientWidth)
  );
}

function maintainScrollVisibility(activeElement, scrollParent) {
  const { offsetHeight } = activeElement;
  const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

  const offsetTop =
    activeElement.getBoundingClientRect().top -
    scrollParent.getBoundingClientRect().top +
    scrollTop;

  const isAbove = offsetTop < scrollTop;
  const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

  if (isAbove) {
    scrollParent.scrollTo(0, offsetTop);
  } else if (isBelow) {
    scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
  }
}
